import { Figure, TextBackground, Reference, CustomLink } from "../components";

/**
 * Define the HTML code for the Method page.
 * @returns HTML code for the Method page
 */
export const Method = () => {
	return (
		<section className="page">
			<TextBackground>
				<Figure
					imgName="method_1.jpeg"
					caption="The STEGG tool."
					width="70%"
					desc={
						"STEGG comprises different processes that increase " +
						"pMHC structural modeling accuracy, expand the pMHC " +
						"structural modeling repertoire, and make the " +
						"tool easier to use by the community."
					}
				/>
			</TextBackground>
			<TextBackground>
				<h2>Overview</h2>
				<div>
					STEGG is a new pMHC structural modeling tool. Compared
					to its predecessor, as well as other pMHC structural
					modeling tools in the literature, it exhibits better
					performance on pMHC structural modeling accuracy. Moreover,
					it expands the pMHC modeling repertoire to cases that we
					were previously unable to model. Such cases include peptides
					exhibiting post-translational modifications, such as
					phosphorylation and citrullination, among others. Other
					cases deal with peptides that exhibit non-canonical peptide
					geometries in the MHC binding cleft, such as peptides
					exhibiting N-terminus/C-terminus extensions. Lastly, all of
					these cases, and more, are able to be modeled through just
					very few clicks through this webserver.
				</div>
			</TextBackground>
			<TextBackground>
				<h2>Algorithm</h2>
				<Figure
					imgName="method_2.jpeg"
					caption="Algorithm."
					desc={
						"STEGG is able to achieve state-of-the-art " +
						"performance over its predecessor through very " +
						"specific changes and contributions. Such changes " +
						"can be seen in the flowchart above, and " +
						"are enumerated below in more detail."
					}
					width="50%"
				/>
				<div style={{ marginInline: "1rem" }}>
					<ol>
						<li>
							<b>Anchor identification module:</b> The anchor
							identification module, given a peptide sequence and
							an MHC allotype, is able to predict the anchor
							configuration of the peptide in the MHC cleft with
							great accuracy. Predictions also extend to
							non-canonical configurations also. As a result, the
							appropriate pMHC template is fetched that can allow
							modeling of non-canonical cases if need be.
						</li>
						<li>
							<b>Concurrent usage of sample and threading:</b> The
							predecessor of STEGG, as well as some other few
							pMHC structural modeling approaches in the
							literature use sampling and scoring padarigms for
							pMHC structural modeling. Other tools emphasize on
							pMHC modeling through a given structural template,
							like homology modeling, among others. STEGG
							uses boht of these paradigms concurrently. We find
							that this allows for more a complete sampling of the
							pMHC conformational space, and resulted in
							state-of-the-art performance.
						</li>
						<li>
							<b>Post-translational modifications:</b> STEGG
							is the first method that can rapidly model peptides
							exhibiting post-translational modifications. This is
							possible through adopting pyTMS, a
							post-translational modification modeling tool.
							Contrary to previous offerings in the literature,
							modeling of post-translational modifications happens
							within minutes.
						</li>
						<li>
							<b>A web interface:</b> We now offer a pMHC modeling
							interface that allows pMHC modeling with a few
							clicks throgh this web server. Local installation of
							STEGG is still possible, however, the web
							server usage is recommended for users not wanting to
							install STEGG locally.
						</li>
					</ol>
				</div>
			</TextBackground>
			<TextBackground id="refs">
				<h2>References</h2>
				<div>
					If you used our web-based tool, or used the STEGG
					docker image or source code, please cite [1]. This webserver
					uses the newest version of STEGG (previous reference),
					which is more accurate and easy-to-use than its predecessor,
					with expanded pMHC structural modeling repertoire to bout.
					If however, you would like to see/cite the original
					publication, use [2].
					<ol>
						<li>
							<Reference
								title="R. Fasoulis, M. M. Rigo, G. Lizée, D. A. Antunes, and L.E. Kavraki, “STEGG: Expanding Rapid Class I Peptide-Major Histocompatibility Complex Modeling to Post-Translational Modifications and Noncanonical Peptide Geometries,” Journal of Chemical Information and Modeling, vol. 64, no. 5, pp. 1730–1750, Mar. 2024."
								pdfLink="https://kavrakilab.org/publications/fasoulis2024-stegg2.pdf"
								publisherLink="https://pubs.acs.org/doi/10.1021/acs.jcim.3c01667"
							/>
						</li>
						<li>
							<Reference
								title="Abella JR, Antunes DA, Clementi C, Kavraki LE. STEGG: A Fast Method for Generating Ensembles of Bound Peptide-MHC Conformations. Molecules. 2019; 24(5):881. https://doi.org/10.3390/molecules24050881"
								pdfLink="https://kavrakilab.org/publications/abella2019-stegg.pdf"
								publisherLink="https://www.mdpi.com/1420-3049/24/5/881"
							/>
						</li>
					</ol>
				</div>
			</TextBackground>
			<TextBackground>
				<h2>Dataset and Repository</h2>
				<div>
					To find the datasets that were used for the
					evaluation/comparison of STEGG to other tools in the
					literature, as well as to access the code of STEGG,
					please use the repo found{" "}
					<CustomLink
						path="https://github.com/KavrakiLab/Ape-Gen2.0"
						title="here."
						newTab
					/>
				</div>
			</TextBackground>
		</section>
	);
};
